import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, messageOf, configError, type MeResponse } from "./api.ts";

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase: SupabaseClient | null =
  url && anonKey
    ? createClient(url, anonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      })
    : null;

type AuthValue = {
  ready: boolean;
  session: Session | null;
  me: MeResponse | null;
  meError: string;
  refreshMe: () => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(!supabase);
  const [session, setSession] = useState<Session | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meError, setMeError] = useState("");

  useEffect(() => {
    if (!supabase) return;
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setReady(true);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setReady(true);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setMe(null);
      setMeError("");
      return;
    }
    let active = true;
    setMeError("");
    void api<MeResponse>("/v1/me", session.access_token)
      .then((next) => {
        if (active) setMe(next);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setMe(null);
        setMeError(messageOf(error));
      });
    return () => {
      active = false;
    };
  }, [session]);

  const value = useMemo<AuthValue>(
    () => ({
      ready,
      session,
      me,
      meError,
      refreshMe: async () => {
        if (!session) return;
        setMeError("");
        try {
          setMe(await api<MeResponse>("/v1/me", session.access_token));
        } catch (error) {
          setMe(null);
          setMeError(messageOf(error));
        }
      },
      signOut: async () => {
        await supabase?.auth.signOut();
        setMe(null);
      },
    }),
    [ready, session, me, meError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing.");
  return value;
}

export function useToken(): string {
  const { session } = useAuth();
  return session?.access_token ?? "";
}

export { configError };
